<template>
  <div>
  <v-expansion-panels
    v-if="toolEvents && toolEvents.length > 0"
    density="compact"
    :class="['tool-events-container', compact ? 'mb-2' : 'mb-4']"
  >
    <v-expansion-panel
      v-for="(event, eventIndex) in toolEvents"
      :key="eventIndex"
      :class="['event-panel', getEventTypeClass(event.type)]"
      bg-color="background"
      elevation="0"
    >
      <v-expansion-panel-title>
        <div class="tool-event-title">
          <v-icon v-if="event.type === 'tool_start'" color="primary" :class="iconMargin" :size="iconSize">mdi-wrench-clock</v-icon>
          <v-icon v-else-if="event.type === 'tool_end'" color="secondary" :class="iconMargin" :size="iconSize">mdi-check-outline</v-icon>
          <v-icon v-else-if="event.type === 'tool_warning'" color="warning" :class="iconMargin" :size="iconSize">mdi-alert-outline</v-icon>
          <v-icon v-else-if="event.type === 'llm_start'" color="primary" :class="iconMargin" :size="iconSize">mdi-timer-play-outline</v-icon>
          <span v-else :class="['event-badge', getEventTypeClass(event.type), iconMargin]">{{ event.type }}</span>
          <span :class="['tool-name', { 'text-caption': compact }]">{{ getToolName(event) }}</span>
          <span :class="['tool-response', 'text-truncate', getEventTypeClass(event.type), { 'text-caption': compact }]">{{ getTruncatedResponse(event) }}</span>
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <vue-json-pretty :data="event.data" :deep="4" theme="dark" />
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
  <transition name="tool-activity-fade">
    <div v-if="showActivity" :class="['tool-activity-banner', compact ? 'mb-2' : 'mb-4']">
      <v-icon size="18" color="primary" class="mr-2">mdi-database-search</v-icon>
      <span :class="{ 'text-caption': compact }">Gathering data...</span>
    </div>
  </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import type { ToolEvent } from '@/types/msq'
import {
  getEventTypeClass,
  getToolName,
  getTruncatedResponse
} from '@/utils/toolEvents'

const props = withDefaults(defineProps<{
  toolEvents: ToolEvent[]
  showActivity?: boolean
  compact?: boolean
}>(), {
  showActivity: false,
  compact: false
})

const iconMargin = computed(() => props.compact ? 'mr-2' : 'mr-4')
const iconSize = computed(() => props.compact ? 'small' : undefined)
</script>

<style lang="scss" scoped>
.tool-events-container {
  max-width: 100%;

  :deep(.v-expansion-panel) {
    max-width: 100%;
  }

  :deep(.v-expansion-panel-title) {
    min-height: 36px;
    padding: 6px 12px;
    overflow: hidden;
  }
}

.event-panel {
  margin-bottom: 4px;
}

.tool-event-title {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.event-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.tool-error .event-badge {
  background-color: #F44336;
}

.tool-warning .event-badge {
  background-color: #FB8C00;
}

.tool-name {
  font-weight: bold;
  margin: 0 8px;
  white-space: nowrap;
}

.tool-response {
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}

.tool-response.tool-warning {
  color: rgb(var(--v-theme-warning));
}

.tool-activity-banner {
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 2px;
  padding: 6px 0;
  font-size: 13px;
  color: rgb(var(--v-theme-primary));

  .v-icon {
    animation: pulse 1.2s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.tool-activity-fade-enter-active {
  transition: opacity 0.18s ease-out, transform 0.18s ease-out;
}

.tool-activity-fade-leave-active {
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.tool-activity-fade-enter-from,
.tool-activity-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
